"use client";
// "use client" hoja interactiva: carrusel minimalista de flujos reales (investigación / redacción / estudio).
// Avance automático suave (6s sincronizado con .stories-progress-bar), pausa en hover/foco/reduced-motion.

import Link from "next/link";
import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  FlaskConical,
  PenLine,
  GraduationCap,
} from "lucide-react";
import { localePath, type TranslationKey } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { Reveal } from "./Reveal";

const AUTO_ADVANCE_MS = 6000;

const STORIES: Array<{
  id: string;
  icon: typeof FlaskConical;
  /** acento propio de la historia (chip, progress, marcadores) */
  accent: string;
  tagKey: TranslationKey;
  titleKey: TranslationKey;
  descKey: TranslationKey;
  stat1ValueKey: TranslationKey;
  stat1LabelKey: TranslationKey;
  stat2ValueKey: TranslationKey;
  stat2LabelKey: TranslationKey;
}> = [
  {
    id: "research",
    icon: FlaskConical,
    accent: "var(--hero-blue)",
    tagKey: "storiesS1Tag",
    titleKey: "storiesS1Title",
    descKey: "storiesS1Desc",
    stat1ValueKey: "storiesS1Stat1Value",
    stat1LabelKey: "storiesS1Stat1Label",
    stat2ValueKey: "storiesS1Stat2Value",
    stat2LabelKey: "storiesS1Stat2Label",
  },
  {
    id: "writing",
    icon: PenLine,
    accent: "var(--hero-magenta)",
    tagKey: "storiesS2Tag",
    titleKey: "storiesS2Title",
    descKey: "storiesS2Desc",
    stat1ValueKey: "storiesS2Stat1Value",
    stat1LabelKey: "storiesS2Stat1Label",
    stat2ValueKey: "storiesS2Stat2Value",
    stat2LabelKey: "storiesS2Stat2Label",
  },
  {
    id: "study",
    icon: GraduationCap,
    accent: "var(--hero-orange)",
    tagKey: "storiesS3Tag",
    titleKey: "storiesS3Title",
    descKey: "storiesS3Desc",
    stat1ValueKey: "storiesS3Stat1Value",
    stat1LabelKey: "storiesS3Stat1Label",
    stat2ValueKey: "storiesS3Stat2Value",
    stat2LabelKey: "storiesS3Stat2Label",
  },
];

export function CustomerStories() {
  const t = useT();
  const locale = useLocale();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Avance automático natural cada 6s (respeta hover, foco, pestaña oculta y prefers-reduced-motion)
  useEffect(() => {
    if (paused) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    let timer: number | undefined;
    const startTimer = () => {
      if (timer !== undefined) window.clearInterval(timer);
      if (typeof document !== "undefined" && document.hidden) return;
      timer = window.setInterval(() => {
        setIndex((prev) => (prev + 1) % STORIES.length);
      }, AUTO_ADVANCE_MS);
    };
    const stopTimer = () => {
      if (timer !== undefined) {
        window.clearInterval(timer);
        timer = undefined;
      }
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        stopTimer();
      } else {
        startTimer();
      }
    };
    startTimer();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stopTimer();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [paused, index]);

  const current = STORIES[index]!;
  const Icon = current.icon;

  return (
    <section
      id="customer-stories"
      className="py-20 sm:py-28 overflow-hidden bg-background text-foreground transition-colors"
      aria-label={t("storiesTitle")}
    >
      <div className="mx-auto max-w-container-wide gutter-fluid">
        <Reveal className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
          <h2 className="text-[var(--step-3)] font-bold tracking-tight text-foreground text-balance">
            {t("storiesTitle")}
          </h2>
          <p className="mt-3.5 text-sm sm:text-base leading-relaxed text-muted-foreground max-w-[52ch] mx-auto text-pretty">
            {t("storiesSubtitle")}
          </p>
        </Reveal>

        <div
          className="relative mx-auto max-w-5xl"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setPaused(false);
            }
          }}
        >
          <div
            key={current.id}
            className="lovable-card relative min-h-[30rem] overflow-hidden rounded-[var(--radius-panel)] p-6 sm:p-8 md:min-h-[22rem] md:p-11 flex flex-col justify-between border border-border/40 bg-[var(--card)]/80 shadow-2xl transition-all duration-300"
          >
            {/* Hairline superior con el acento dinámico de la historia */}
            <div
              className="pointer-events-none absolute inset-x-8 top-0 h-px transition-colors duration-500"
              style={{
                background: `linear-gradient(90deg, transparent, color-mix(in srgb, ${current.accent} 40%, transparent), transparent)`,
              }}
              aria-hidden="true"
            />

            <div className="relative grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12 items-center my-auto">
              {/* Left: workflow narrative */}
              <div className="md:col-span-7 flex flex-col justify-between">
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div className="inline-flex min-w-0 items-center gap-2.5 rounded-full border border-border/80 bg-muted/30 px-3.5 py-1.5 shadow-2xs backdrop-blur-xs">
                    <Icon
                      className="size-3.5 shrink-0 transition-colors duration-300"
                      style={{ color: current.accent }}
                      aria-hidden="true"
                    />
                    <span
                      className="truncate text-xs font-semibold tracking-tight text-foreground/90"
                      title={t(current.tagKey)}
                    >
                      {t(current.tagKey)}
                    </span>
                  </div>

                  <span
                    className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground"
                    aria-hidden="true"
                  >
                    {String(index + 1).padStart(2, "0")} /{" "}
                    {String(STORIES.length).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="text-balance text-[var(--step-1)] font-bold leading-[1.18] tracking-tight text-foreground">
                  {t(current.titleKey)}
                </h3>

                <p className="mt-3.5 max-w-[50ch] text-[15px] leading-relaxed text-muted-foreground sm:text-base text-pretty">
                  {t(current.descKey)}
                </p>

                {/* CTA */}
                <div className="mt-8">
                  <Link
                    href={localePath(locale, "/chat")}
                    className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold tracking-tight text-background shadow-xs transition-all duration-200 hover:-translate-y-px hover:shadow-md active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none motion-reduce:transform-none"
                  >
                    <span>{t("storiesCta")}</span>
                    <ArrowUpRight className="size-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>

              {/* Right: verified capacity metrics con marcador de acento */}
              <div className="flex flex-col justify-center gap-0 border-t border-border/40 pt-4 md:col-span-5 md:border-s md:border-t-0 md:ps-10 md:pt-0">
                <div className="flex items-start gap-4 py-5 first:pt-0 last:pb-0 md:py-6">
                  <span
                    className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-md bg-foreground/[0.05] text-[11px] font-mono font-semibold text-muted-foreground"
                    aria-hidden="true"
                  >
                    01
                  </span>
                  <div className="space-y-1">
                    <div className="text-[var(--step-2)] font-bold tracking-tight text-foreground tabular-nums">
                      {t(current.stat1ValueKey)}
                    </div>
                    <div
                      className="break-words px-2 text-xs leading-relaxed text-muted-foreground"
                      title={t(current.stat1LabelKey)}
                    >
                      {t(current.stat1LabelKey)}
                    </div>
                  </div>
                </div>
                <div className="h-px bg-border/40" aria-hidden="true" />
                <div className="flex items-start gap-4 py-5 last:pb-0 md:py-6">
                  <span
                    className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-md bg-foreground/[0.05] text-[11px] font-mono font-semibold text-muted-foreground"
                    aria-hidden="true"
                  >
                    02
                  </span>
                  <div className="space-y-1">
                    <div className="text-[var(--step-2)] font-bold tracking-tight text-foreground tabular-nums">
                      {t(current.stat2ValueKey)}
                    </div>
                    <div
                      className="break-words px-2 text-xs leading-relaxed text-muted-foreground"
                      title={t(current.stat2LabelKey)}
                    >
                      {t(current.stat2LabelKey)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Segmented bar indicators con progreso continuo */}
          <div
            className="mt-5 flex items-center justify-center gap-2"
            role="group"
            aria-label={t("storiesTitle")}
          >
            {STORIES.map((story, idx) => {
              const isActive = idx === index;
              return (
                <button
                  key={story.id}
                  type="button"
                  onClick={() => setIndex(idx)}
                  aria-label={t(story.tagKey)}
                  aria-current={isActive || undefined}
                  className="flex min-h-6 min-w-12 items-center justify-center rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                >
                  <span className="relative block h-[3px] w-14 overflow-hidden rounded-full bg-foreground/10 sm:w-16">
                    <span
                      key={isActive ? `active-${index}` : `idle-${idx}`}
                      className={`absolute inset-y-0 start-0 rounded-full ${
                        isActive
                          ? "stories-progress-bar"
                          : idx < index
                            ? "bg-foreground/30 w-full"
                            : "w-0"
                      }`}
                      style={
                        isActive
                          ? {
                              background: current.accent,
                              animationPlayState: paused ? "paused" : "running",
                            }
                          : undefined
                      }
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
